import type { CurrenciesData, UriData } from "@broadside/common";
import { NextSeo } from "next-seo";
import { useMemo } from "react";
import { Yacht404Message } from "../components/Yacht404Message";
import { useUriPageSeo } from "../uri-seo";
import { YachtSearchFilterProvider } from "../../search";
import { CurrencyConversionProvider } from "../../currency";
import { ArticlePage } from "./ArticlePage";
import { NotFoundErrorPage } from "./ErrorPage";
import { YachtPage } from "./YachtPage";
import { YachtSearchPage } from "./YachtSearchPage";


export type UriPageProps = {
	page: UriData | null;
	currencies: CurrenciesData;
};

/**
 * Page for an Article or Yacht with data fetched from Broadside-API's
 * `/uri` endpoint.
 */
export const UriPage = ({ page, currencies }: UriPageProps) => {
	const seo = useUriPageSeo(page);

	const component = useMemo(() => {
		if(!page) {
			return <NotFoundErrorPage/>;
		}

		if(!page.data) {
			switch(page.type) {
				case "article":
					return <NotFoundErrorPage/>;
				case "yacht":
					return (
						<YachtSearchPage
							extraTopContent={ <Yacht404Message/> }
							currencies={ currencies }
						/>
					);
			}
		}

		switch(page.type) {
			case "article":
				return <ArticlePage article={ page.data } headerLinks={ page.headerLinks } footerLinks={ page.footerLinks }/>;
			case "yacht":
				return <YachtPage yacht={ page.data } headerLinks={ page.headerLinks } footerLinks={ page.footerLinks }/>;
		}
	}, [ currencies, page ]);

	return (
		<YachtSearchFilterProvider>
			<CurrencyConversionProvider data={ currencies }>
				<NextSeo { ...seo }/>
				{ component }
			</CurrencyConversionProvider>
		</YachtSearchFilterProvider>

	);
};
UriPage.displayName = "UriPage";
